<template>
	<div class="inner-html-container">
		<div class="page" :style="isMobile ? {width: '100vw', height: '80vh'} : {width: '86vw', height: '90vh'}" aria-label="当前为AI助教页面，包括对话管理和聊天区两部分。对话管理模块中，点击新建对话按钮，会创建一个新对话，您可以点击不同的对话进入相应的聊天页面，每个对话右侧都有一个重命名按钮和删除按钮，您可以根据需要修改对话的名称或者删除这段对话。聊天区域中，您可以在文本框中输入您的问题，完成后点击发送按钮即可。请注意，AI每次回复需要一定时间，在没有完整回答完问题前，您听到的回答可能不完整，且在此期间，您将不能发送新的问题，直到AI回复结束。" tabindex="0">
			<div class="tips">
				<div class="title" v-if="isMobile">
					<BackButton />
				</div>
				<div @click="handleClearStorage" v-if="isMobile" class="pointer" role="button">清空</div>
			</div>
			<div class="grid-space-between" :class="!isMobile ? 'grid-box' : ''">
				<div class="left-container" v-if="!isMobile">
					<BackButton style="width: 100%; height: 40px" />
					<el-button
						type="success"
						style="width: 100%"
						@click="handleAddSession"
						tabindex="0"
					>
						新建对话
					</el-button>
					<div class="session-area">
						<div
							class="session-item"
							:class="activeIndex == index ? 'session-item-active' : ''"
							v-for="(item, index) in sessionList"
							:key="index"
							@click="handleChangeSessionIndex(index)"
							@keydown.enter="handleChangeSessionIndex(index)"
						>
							<span
								:class="activeIndex == index ? 'active-node' : 'normal-node'"
								v-if="editIndex != index"
								role="button"
								:aria-label="item.title+(activeIndex == index ? '已选中' : '')"
								tabindex="0"
							>
								{{ item.title }}
							</span>
							<el-input
								:ref="`renameRef_${index}`"
								autofocus
								v-model="item.title"
								v-else
								size="small"
								style="width: 120px"
								@blur="editIndex = -1"
								@change="editIndex = -1"
							/>
							<div class="icon-box">
								<!-- 重命名 -->
								<el-icon
									class="icon"
									color="#E6A23C"
									@click.stop="handleFocusInput(index)"
									@keydown.enter="handleFocusInput(index)"
									:aria-label="'重命名'+item.title"
									role="button"
									tabindex="0"
								>
									<BrushFilled />
								</el-icon>
								<!-- 删除 -->
								<el-icon
									class="icon"
									color="#F56C6C"
									@click.stop="handleDeleteSession(index)"
									@keydown.enter="handleDeleteSession(index)"
									role="button"
									:aria-label="'删除'+item.title"
									tabindex="0"
								>
									<DeleteFilled />
								</el-icon>
							</div>
						</div>
					</div>
				</div>
				<div class="chat-container">
					<div class="message-area">
						<MessageComp
							ref="messageRef"
							:message="queryInfos.messages"
							:accessibilityPromptLength="accessibilityPrompt.length"
						></MessageComp>
					</div>
					<div :class="['input-area', isMobile ? 'left-space' : '', editMode ? 'editMode' : 'audioMode']">
						<el-button circle v-if="editMode" @click="editMode = !editMode" :aria-label="'键盘输入，'+(isMobile ? '双击' : '回车')+'切换至语音输入。'">
							<el-icon style="font-size: 20px;"><Edit /></el-icon>
						</el-button>
						<el-button circle v-else @click="editMode = !editMode" :aria-label="'语音输入，'+(isMobile ? '双击' : '回车')+'切换至键盘输入。'">
							<el-icon style="font-size: 20px;"><Microphone /></el-icon>
						</el-button>
						<el-input
							v-if="editMode"
							v-model="queryKeys"
							id="keyInput"
							placeholder="请输入内容"
						/>
						<el-button
							v-else
							@touchstart="startRecording()"
							@touchend="stopRecording()"
							@keydown.space="startRecording()"
							@keyup.space="stopRecording()"
							:disabled="loading"
						>{{ isMobile ? '按住说话' : '按住空格说话' }}</el-button>
						<el-button
							v-if="editMode"
							style="height: 40px"
							type="primary"
							@click="handleRequest"
							:disabled="!queryKeys"
							:loading="loading"
							aria-label="发送"
							tabindex="0"
						>
						<el-icon><Promotion /></el-icon>
						</el-button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import OpenAI from "openai";
	import MessageComp from "@/components/MessageComp.vue";
	import { Microphone, Edit, Promotion, DeleteFilled, BrushFilled } from "@element-plus/icons-vue";
	import { ElMessage, ElMessageBox } from "element-plus";
	import MobileDetect from "mobile-detect";
import BackButton from '@/components/BackButton.vue';

	export default {
		components: { MessageComp, Microphone, Edit, Promotion, DeleteFilled, BrushFilled, BackButton },
		data() {
			return {
				isMobile: false, // 当前设备是不是手机
				renameValue: "",
				sessionList: [],
				activeIndex: -1,
				editIndex: -1, // 当前重命名的序号
				editMode: true,
				recognition: null,
				isRecording: false,
				queryKeys: "",
				openai: null,
				loading: false,
				accessibilityPrompt: "\n注意：你是一个特教专家，你知道如何为具有视力障碍的学生讲解学习中的知识点。接下来的所有回答，请你遵循下列要求：\n1. 请将所有腧穴符号或者理科中的符号（如根号，派，三角函数符号等）转换为中文，确保屏幕阅读器用户可以通过听觉了解公式内容。\n2. 请不要使用markdown排版，输出纯文本即可。",
				queryInfos: {
					// { role: "assistant", content: "You are a helpful assistant." }
					// {"role": "user", "content": "What's the highest mountain in the world?"}
					messages: [],
					model: `deepseek-chat`,
					stream: true,
				},
				isKeyIn: false,
			};
		},
		created() {
			this.initSessionList();
			this.initIndex();
			this.initOpenAI();
			// 监测当前设备是不是手机
			const md = new MobileDetect(window.navigator.userAgent);
			this.isMobile = md.mobile();
			if ("webkitSpeechRecognition" in window) {
				this.recognition = new window.webkitSpeechRecognition();
				this.recognition.continuous = true;
				this.recognition.interimResults = false;
				this.recognition.lang = "zh-CN";
				this.recognition.onresult = (event) => {
					this.queryKeys += event.results[event.results.length-1][0].transcript;
				};
				this.recognition.onend = () => {
					this.handleRequest();
				};
				this.recognition.onerror = (event) => {
					console.error("语音识别错误: ", event.error);
				};
			} else {
				alert("当前浏览器不支持语音识别，您无法使用语音输入功能。请使用其他浏览器（如谷歌或Edge浏览器）。");
			}
			if ('speechSynthesis' in window) {
				this.speechSynthesis=window.speechSynthesis;
				window.addEventListener('keydown', () => {
					this.speechSynthesis.cancel();
				});
			} else {
				alert("当前浏览器暂不支持AI回复自动播报功能。请使用其他浏览器（如谷歌或Edge浏览器）。");
			}
		},
		watch: {
			sessionList: {
				handler(val) {
					const list = val.map((o, i) => {
						if (i == this.activeIndex) {
							return {
								...o,
								messages: this.queryInfos.messages,
							};
						} else {
							return { ...o };
						}
					});
					localStorage.setItem("list", JSON.stringify(list));
				},
				deep: true,
				immediate: false,
			},
			activeIndex: {
				handler(val) {
					localStorage.setItem("index", JSON.stringify(val));
				},
				deep: true,
				immediate: false,
			},
		},
		methods: {
			// 手机端清空对话
			handleClearStorage() {
				localStorage.removeItem("list");
				localStorage.removeItem("index");
				this.queryInfos.messages = [];
				this.sessionList = [];
				this.activeIndex = -1;
			},
			// 初始化对话列表
			initSessionList() {
				this.sessionList = JSON.parse(localStorage.getItem("list") || "[]");
			},
			// 初始化当前序号
			initIndex() {
				const listLen = JSON.parse(localStorage.getItem("list") || "[]").length; // 对话列表
				const lastIndex = JSON.parse(localStorage.getItem("index") || "-1"); // 当前对话的序号
				if (listLen) {
					this.activeIndex = lastIndex || 0;
				} else {
					this.activeIndex = -1;
				}
				if (this.activeIndex != -1) {
					this.queryInfos.messages =
						this.sessionList[this.activeIndex].messages || []; // 获取当前对话历史记录
				}
			},
			// 新增session对话
			handleAddSession() {
				if (this.loading) {
					ElMessage({ type: "warning", message: "请当前问题查询完成后重试！" });
					return;
				}
				this.sessionList.push({
					title: `对话${this.sessionList.length + 1}`,
					crtTime: new Date(),
					messages: []
				});
				this.queryInfos.messages = [];
				this.activeIndex = this.sessionList.length - 1;
			},
			// 删除对话
			handleDeleteSession(index = 0) {
				ElMessageBox.confirm("确认删除当前对话？", "警告", {
					confirmButtonText: "确定",
					cancelButtonText: "取消",
					type: "warning",
				}).then(() => {
					// 删除指定的对话
					this.sessionList.splice(index, 1);
					/**
					 * 逻辑1:删除的当前选中的对话框，判断新的当前元素（原来的+1）是否存在，不存在则向前抓
					 * 逻辑2:删除的非当前选中的(只判断小于，因为大于不影响当前展示内容)
					 */
					if (index == this.activeIndex) {
						// 初始化index
						this.activeIndex = this.sessionList[index] ? index : --index;
					} else if (index < this.activeIndex) {
						this.activeIndex = --this.activeIndex;
					}
					// 更新页面数据展示
					this.queryInfos.messages =
						this.activeIndex > -1 ? this.sessionList[this.activeIndex] : [];
					this.handleChangeSessionIndex(this.activeIndex);
				}).catch((action) => {
					console.log("error" + action);
				});
			},
			handleFocusInput(index) {
				const _this = this;
				this.editIndex = index;
				this.$nextTick(() => {
					_this.$refs[`renameRef_${index}`][0].focus();
				});
			},
			// 切换session对话序号
			handleChangeSessionIndex(index) {
				if (this.loading) {
					ElMessage({ type: "warning", message: "请当前问题查询完成后重试！" });
					return;
				}
				this.activeIndex = index;
				this.queryInfos.messages =
					this.sessionList[this.activeIndex]?.messages || [];
			},
			initOpenAI() {
				this.openai = new OpenAI({
					baseURL: "https://api.deepseek.com",
					apiKey: "sk-3c06c6e636194af7b8f674ac100af764",
					dangerouslyAllowBrowser: true,
				});
			},
			// 点击进行提问
			async handleRequest() {
				if (!this.queryKeys) return;
				if (!this.openai) this.initOpenAI();
				// 查询当前是否存在对话窗口，不存在的话，新建一个对话窗口
				if (!this.sessionList.length) {
					await this.handleAddSession();
				}
				// 1.向对话窗口添加对话信息(请求)
				this.queryInfos.messages.push({
					role: "user",
					content: this.queryKeys+this.accessibilityPrompt,
				});
				this.queryKeys = "";
				// 滚动到底部
				this.$refs.messageRef.scrollBottom();

				try {
					// 2.请求服务进行提问
					this.loading = true;
					const response = await this.openai.chat.completions.create(
						this.queryInfos
					);
					this.queryInfos.messages.push({ role: "assistant", content: "" });
					for await (let part of response) {
						part=part.choices[0].delta.content;
						this.queryInfos.messages[
							this.queryInfos.messages.length - 1
						].content += part;
						this.$refs.messageRef.scrollBottom();
					}
					this.startSpeaking();
					// 更新缓存数据
					this.sessionList[this.activeIndex].messages = this.queryInfos.messages;
					this.loading = false;
				} catch (error) {
					this.loading = false;
					this.queryInfos.messages.push({
						role: "assistant",
						content: "加载失败,请稍后重试!",
					});
				}
			},
			startRecording() {
				if (this.isRecording | !this.recognition) return;
				this.isRecording=true;
				this.recognition.start();
			},
			stopRecording() {
				if (!this.recognition) return;
				this.isRecording=false;
				this.recognition.stop();
			},
			startSpeaking() {
				if (!this.speechSynthesis) return;
				const utterance = new SpeechSynthesisUtterance(this.queryInfos.messages[this.queryInfos.messages.length - 1].content);
				utterance.lang = 'zh-CN'; // 设置语言为中文
				this.speechSynthesis.speak(utterance);
			},
			stopSpeaking() {
				if (!this.speechSynthesis) return;
				this.speechSynthesis.cancel();
			}
		},
	};
</script>


<style scoped lang="scss">
	.pointer {
		cursor: pointer;
	}
	.inner-html-container {
		width: 100vw;
		height: 100vh;
		display: flex;
		justify-content: center;
		align-items: center;
		background: rgb(216.8, 235.6, 255);
		.page {
			background: #fff;
			box-shadow: 0 0 12px 12px rgb(197.7, 225.9, 255);
			box-sizing: border-box;
			border-radius: 8px;
			overflow: hidden;
			.tips {
				width: 100%;
				height: 32px;
				background: rgb(121.3, 187.1, 255);
				color: #fff;
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: 0 12px;
				box-sizing: border-box;
				.title {
					font-size: 16px;
				}
				.desc {
					font-size: 14px;
					color: rgb(216.8, 235.6, 255);
				}
			}
			.grid-box {
				display: grid;
				grid-template-columns: 24% auto;
				column-gap: 12px;
			}
			.grid-space-between {
				width: 100%;
				height: calc(100% - 48px);
				.left-container {
					background-color: #f7f8fa;
					padding: 12px;
					height: calc(90vh - 32px - 24px); // 顶部比标题、padding边框
					// 对话列表的展示区域
					.session-area {
						width: 100%;
						height: calc(90vh - 32px - 24px - 12px - 32px); // 边框+按钮
						margin-top: 12px;
						box-sizing: border-box;
						overflow-y: auto;
						.session-item {
							height: 36px;
							line-height: 36px;
							margin-bottom: 8px;
							border-radius: 4px;
							padding: 0 18px;
							background: rgb(199.5, 201, 204);
							color: #f4f5f5;
							display: flex;
							justify-content: space-between;
							align-items: center;
							cursor: pointer;
							.normal-node {
								padding-left: 14px;
							}
							.icon-box {
								display: grid;
								column-gap: 8px;
								grid-template-columns: repeat(2, 1fr);
								.icon {
									cursor: pointer;
								}
							}
						}
						.session-item-active {
							background: rgb(51.2, 126.4, 204);
							color: #f4f5f5;
							.active-node {
								display: flex;
								align-items: center;
								&::before {
									display: inline-block;
									content: "";
									width: 6px;
									height: 6px;
									background: #fff;
									border-radius: 50%;
									margin-right: 8px;
								}
							}
						}
					}
				}
			}
			.chat-container {
				width: 100%;
				height: calc(90vh - 32px);
				background: #fff;
				border-radius: 8px;
				.message-area {
					height: calc(100% - 40px - 16px - 44px);
					width: calc(100% - 32px);
					padding: 16px 16px 0 16px;
				}
				// 手机端留左侧边框
				.left-space {
					margin-left: 16px;
				}
				.input-area {
					height: 40px;
					display: grid;
					box-sizing: border-box;
					background: #fff;
					padding: 0 16px 16px 0;
				}
				.editMode {
					grid-template-columns: 40px auto 90px;
				}
				.audioMode {
					grid-template-columns: 40px auto;
				}
			}
		}
	}
</style>
